import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, CircularProgress } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { authGet } from '../../../services/apiService';
import { isAdmin } from '../../../services/authService';

const API = '/api/datadog-monitors/trends';
const MONTHS_API = '/api/datadog-monitors/monthly-report/months';
const MONTH_KEY = 'YYYY-MM';

// The saved report for each month, kept for the life of the browser page (the panel is unmounted whenever another tab is
// selected, so this lives outside it). A report is about 40 MB, so only the most recent few are kept. An entry is used only
// while its savedAt matches the one the server lists, so generating the month again makes it load afresh.
const MAX_CACHED_MONTHS = 3;
const reportCache = new Map();
const cachedReport = (key, savedAt) => {
  const hit = reportCache.get(key);
  if (!hit || hit.savedAt !== savedAt) return undefined;
  // Re-insert so the Map's order is least recently used first.
  reportCache.delete(key);
  reportCache.set(key, hit);
  return hit.html;
};
const cacheReport = (key, savedAt, html) => {
  reportCache.set(key, { savedAt, html });
  while (reportCache.size > MAX_CACHED_MONTHS) reportCache.delete(reportCache.keys().next().value);
};

/**
 * Content of the Trends tab: the overall monthly report for one month, as last saved by Generate Monthly Report (it is
 * never built here, so it only changes when the report is generated again). Shown in an iframe because the report is a
 * complete page with its own styles and script. The month starts as the latest one with a saved report, and only months
 * with fetched days can be picked.
 */
export default function TrendsPanel() {
  // The months that can be reported (the same list as the Generate Monthly Report prompt), and the one shown.
  const [available, setAvailable] = useState({ loaded: false, keys: new Set(), saved: new Map(), min: null, max: null });
  const [month, setMonth] = useState(null);
  const [state, setState] = useState({ loading: true, error: '', html: '' });

  useEffect(() => {
    let cancelled = false;
    authGet(MONTHS_API, null, 'json', { useCache: false })
      .then((data) => {
        if (cancelled) return;
        const keys = (data.months || []).map((m) => m.month).sort();
        const saved = new Map((data.months || []).filter((m) => m.savedAt).map((m) => [m.month, m.savedAt]));
        setAvailable({ loaded: true, keys: new Set(keys), saved, min: keys.length ? dayjs(`${keys[0]}-01`) : null, max: keys.length ? dayjs(`${keys[keys.length - 1]}-01`) : null });
        // The latest month with a saved report; when none is saved, the latest fetched month (which says it has no report).
        const latestSaved = keys.filter((k) => saved.has(k)).pop();
        if (keys.length) setMonth(dayjs(`${latestSaved || keys[keys.length - 1]}-01`));
        else setState({ loading: false, error: 'Nothing has been fetched yet. Fetch monitor events first.', html: '' });
      })
      .catch((err) => { if (!cancelled) setState({ loading: false, error: err.message || 'Failed to list the months', html: '' }); });
    return () => { cancelled = true; };
  }, []);

  const monthKey = month && month.isValid() ? month.format(MONTH_KEY) : '';
  useEffect(() => {
    // Nothing to load until a month with a saved report is picked.
    const savedAt = available.saved.get(monthKey);
    if (!monthKey || !savedAt) {
      setState((prev) => (prev.error || prev.html || prev.loading ? { loading: false, error: '', html: '' } : prev));
      return undefined;
    }
    const cached = cachedReport(monthKey, savedAt);
    if (cached !== undefined) {
      setState({ loading: false, error: '', html: cached });
      return undefined;
    }
    let cancelled = false;
    // The previous month's report is dropped, not left on screen under the new month's name.
    setState({ loading: true, error: '', html: '' });
    authGet(API, { month: monthKey }, 'blob', { useCache: false })
      .then((blob) => blob.text())
      .then((html) => {
        cacheReport(monthKey, savedAt, html);
        if (!cancelled) setState({ loading: false, error: '', html });
      })
      .catch((err) => { if (!cancelled) setState({ loading: false, error: err.message || 'Failed to load the trends report', html: '' }); });
    return () => { cancelled = true; };
  }, [monthKey, available]);

  // The report sizes the frame to its own height so the page scrolls, not the frame. It also needs to know which
  // part of the frame is on screen, to place its pop-ups there.
  const frameRef = useRef(null);
  const [frameHeight, setFrameHeight] = useState(null);
  const sendView = useCallback(() => {
    const frame = frameRef.current;
    if (!frame || !frame.contentWindow) return;
    frame.contentWindow.postMessage({ type: 'trends-view', top: -frame.getBoundingClientRect().top, h: window.innerHeight }, '*');
  }, []);
  useEffect(() => {
    const onMessage = (e) => {
      if (!frameRef.current || e.source !== frameRef.current.contentWindow || !e.data) return;
      if (e.data.type === 'trends-height' && Number.isFinite(e.data.h)) setFrameHeight(e.data.h);
      if (e.data.type === 'trends-ready') sendView();
    };
    window.addEventListener('message', onMessage);
    window.addEventListener('scroll', sendView, true);
    window.addEventListener('resize', sendView);
    return () => {
      window.removeEventListener('message', onMessage);
      window.removeEventListener('scroll', sendView, true);
      window.removeEventListener('resize', sendView);
    };
  }, [sendView]);

  const { loading, error, html } = state;
  const savedAt = available.saved.get(monthKey);
  const monthLabel = month && month.isValid() ? month.format('MMMM YYYY') : '';
  return (
    <Box sx={{ minWidth: 960 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Month"
            views={['year', 'month']}
            openTo="month"
            format="MMMM YYYY"
            value={month}
            onChange={setMonth}
            minDate={available.min || undefined}
            maxDate={available.max || undefined}
            shouldDisableMonth={(d) => !available.keys.has(d.format(MONTH_KEY))}
            shouldDisableYear={(d) => ![...available.keys].some((k) => k.startsWith(`${d.year()}-`))}
            disabled={!available.keys.size}
            slotProps={{ textField: { size: 'small', sx: { width: 220 }, helperText: 'Months with fetched days' } }}
          />
        </LocalizationProvider>
        {loading && <CircularProgress size={16} aria-label="Loading the trends report" />}
      </Box>
      {available.loaded && available.keys.has(monthKey) && !error && (savedAt ? (
        <Alert severity="info" sx={{ mb: 1.5, maxWidth: 720 }}>
          The trend data for {monthLabel} was saved {new Date(savedAt).toLocaleString()}. It only changes when Generate Monthly Report is run for that month again.
        </Alert>
      ) : (
        <Alert severity="warning" sx={{ mb: 2, maxWidth: 720 }}>
          No report has been saved for {monthLabel}. {isAdmin() ? 'Use Generate Monthly Report on the Generate Report tab to create it.' : 'Ask an admin to generate it.'}
        </Alert>
      ))}
      {error && <Alert severity="error" sx={{ mb: 2, maxWidth: 720 }}>{error}</Alert>}
      {html && !error && (
        <Box
          component="iframe"
          title="Monthly trends report"
          ref={frameRef}
          srcDoc={html}
          sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
          sx={{ display: 'block', width: '100%', height: frameHeight || '75vh', minHeight: 600, border: '1px solid #dfe3e6', borderRadius: '10px', bgcolor: '#fff' }}
        />
      )}
    </Box>
  );
}
